// Relatório de Custos - Gestão de Partilha de Custos entre Janeira e Parceiros
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Euro, TrendingUp, TrendingDown, Building2, ArrowLeftRight,
  Download, Filter, RefreshCw, ChevronRight, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const RelatorioCustos = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [parcerias, setParcerias] = useState([]);
  
  // Filtros
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(new Date().toISOString().split('T')[0]);
  const [parceriaFiltro, setParceriaFiltro] = useState('todas');
  
  const [activeTab, setActiveTab] = useState('resumo');

  useEffect(() => {
    loadParcerias();
  }, []);

  useEffect(() => {
    loadRelatorio();
  }, [dataInicio, dataFim, parceriaFiltro]);

  const loadParcerias = async () => {
    try {
      const response = await api.get('/parcerias/entidades');
      setParcerias(response.data.filter(p => p.tipo_parceria === 'empresa'));
    } catch (error) {
      console.error('Erro ao carregar parcerias:', error);
    }
  };

  const loadRelatorio = async () => {
    setLoading(true);
    try {
      let url = `/parcerias/relatorio-custos?data_inicio=${dataInicio}&data_fim=${dataFim}`;
      if (parceriaFiltro && parceriaFiltro !== 'todas') {
        url += `&parceria_id=${parceriaFiltro}`;
      }
      const response = await api.get(url);
      setRelatorio(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast.error('Erro ao carregar relatório de custos');
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    if (!relatorio) return;
    
    let csv = 'Relatório de Custos - Parcerias\n';
    csv += `Período: ${dataInicio} a ${dataFim}\n\n`;
    
    // Resumo por parceria
    csv += 'RESUMO POR PARCERIA\n';
    csv += 'Parceria;Tipo Custo;Total Descontos;Janeira Paga;Parceiro Paga;Saldo;Vales J→P;Vales P→J\n';
    relatorio.resumo_por_parceria.forEach(r => {
      csv += `${r.nome_parceria};${r.tipo_custo};€${r.total_descontos};€${r.total_janeira_paga};€${r.total_parceiro_paga};€${r.saldo_parceiro};${r.vales_janeira_parceiro};${r.vales_parceiro_janeira}\n`;
    });
    
    csv += '\nDETALHE TRANSAÇÕES\n';
    csv += 'Vale;Cliente;Parceria;Direção;Data;Desconto;Janeira Paga;Parceiro Paga;Fatura\n';
    relatorio.detalhe_transacoes.forEach(t => {
      csv += `${t.numero_vale};${t.nome_cliente || '-'};${t.nome_parceria};${t.direcao_label};${t.data_utilizacao?.split('T')[0] || '-'};€${t.valor_desconto};€${t.janeira_paga};€${t.parceiro_paga};${t.numero_fatura_validacao || '-'}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_custos_${dataInicio}_${dataFim}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado com sucesso');
  };

  const getTipoCustoLabel = (tipo) => {
    const labels = {
      'unilateral_janeira': 'Janeira suporta tudo',
      'unilateral_parceiro': 'Parceiro suporta tudo',
      'bilateral_percentagem': 'Partilha %',
      'bilateral_fixo': 'Partilha €'
    };
    return labels[tipo] || tipo;
  };

  const getSaldoBadge = (saldo) => {
    if (saldo > 0) {
      return (
        <Badge variant="success" className="flex items-center gap-1">
          <TrendingUp className="h-3 w-3" />
          Parceiro deve €{saldo.toFixed(2)}
        </Badge>
      );
    } else if (saldo < 0) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <TrendingDown className="h-3 w-3" />
          Janeira deve €{Math.abs(saldo).toFixed(2)}
        </Badge>
      );
    }
    return <Badge variant="secondary">Equilibrado</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="relatorio-custos">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Euro className="h-5 w-5" />
            Relatorio de Custos
          </h2>
          <p className="text-sm text-muted-foreground">
            Acompanhe quem paga o quê em cada parceria bilateral
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadRelatorio} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button onClick={exportarCSV} disabled={!relatorio}>
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-xs">Data Início</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <Label className="text-xs">Data Fim</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <Label className="text-xs">Parceria</Label>
              <Select value={parceriaFiltro} onValueChange={setParceriaFiltro}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as parcerias</SelectItem>
                  {parcerias.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.nome_entidade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Totais Gerais */}
      {relatorio && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Total Descontos</div>
              <div className="text-2xl font-bold text-blue-600">
                €{relatorio.totais.total_descontos.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {relatorio.totais.total_vales} vales utilizados
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Janeira Pagou</div>
              <div className="text-2xl font-bold text-amber-600">
                €{relatorio.totais.total_janeira_paga.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {((relatorio.totais.total_janeira_paga / relatorio.totais.total_descontos) * 100 || 0).toFixed(1)}% do total
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Parceiros Pagaram</div>
              <div className="text-2xl font-bold text-green-600">
                €{relatorio.totais.total_parceiro_paga.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {((relatorio.totais.total_parceiro_paga / relatorio.totais.total_descontos) * 100 || 0).toFixed(1)}% do total
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Parcerias Ativas</div>
              <div className="text-2xl font-bold">
                {relatorio.resumo_por_parceria.length}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                com transações no período
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="resumo">
            <Building2 className="h-4 w-4 mr-2" />
            Resumo por Parceria
          </TabsTrigger>
          <TabsTrigger value="detalhe">
            <FileText className="h-4 w-4 mr-2" />
            Detalhe Transações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resumo">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Resumo por Parceria</CardTitle>
              <CardDescription>
                Visão geral de custos e saldos por parceria
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : !relatorio || relatorio.resumo_por_parceria.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Euro className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma transação encontrada no período selecionado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parceria</TableHead>
                      <TableHead>Tipo Custo</TableHead>
                      <TableHead className="text-right">Total Descontos</TableHead>
                      <TableHead className="text-right">Janeira Paga</TableHead>
                      <TableHead className="text-right">Parceiro Paga</TableHead>
                      <TableHead className="text-center">Vales J→P</TableHead>
                      <TableHead className="text-center">Vales P→J</TableHead>
                      <TableHead>Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {relatorio.resumo_por_parceria.map((r) => (
                      <TableRow key={r.parceria_id}>
                        <TableCell className="font-medium">{r.nome_parceria}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {getTipoCustoLabel(r.tipo_custo)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          €{r.total_descontos.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-amber-600">
                          €{r.total_janeira_paga.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600">
                          €{r.total_parceiro_paga.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="text-sm">
                            <span className="font-medium">{r.vales_janeira_parceiro}</span>
                            <span className="text-xs text-muted-foreground ml-1">
                              (€{r.valor_janeira_parceiro.toFixed(2)})
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="text-sm">
                            <span className="font-medium">{r.vales_parceiro_janeira}</span>
                            <span className="text-xs text-muted-foreground ml-1">
                              (€{r.valor_parceiro_janeira.toFixed(2)})
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{getSaldoBadge(r.saldo_parceiro)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="detalhe">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detalhe de Transações</CardTitle>
              <CardDescription>
                Lista completa de vales utilizados com valores de cada parte
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : !relatorio || relatorio.detalhe_transacoes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma transação encontrada</p>
                </div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vale</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Parceria</TableHead>
                        <TableHead>Direção</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Desconto</TableHead>
                        <TableHead className="text-right">Janeira</TableHead>
                        <TableHead className="text-right">Parceiro</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {relatorio.detalhe_transacoes.map((t, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs">{t.numero_vale}</TableCell>
                          <TableCell>{t.nome_cliente || '-'}</TableCell>
                          <TableCell>{t.nome_parceria}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={t.direcao === 'janeira_parceiro' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {t.direcao === 'janeira_parceiro' ? 'J→P' : 'P→J'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {t.data_utilizacao ? new Date(t.data_utilizacao).toLocaleDateString('pt-PT') : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            €{t.valor_desconto.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-amber-600">
                            €{t.janeira_paga.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-600">
                            €{t.parceiro_paga.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Legenda */}
      <Card>
        <CardContent className="pt-4">
          <div className="text-sm text-muted-foreground">
            <strong>Legenda:</strong>
            <ul className="mt-2 space-y-1">
              <li><Badge variant="default" className="mr-2">J→P</Badge> Cliente comprou no Janeira, usou desconto no Parceiro</li>
              <li><Badge variant="secondary" className="mr-2">P→J</Badge> Cliente comprou no Parceiro, usou desconto no Janeira</li>
              <li><Badge variant="success" className="mr-2">Parceiro deve</Badge> Saldo positivo: o parceiro tem valor a pagar ao Janeira</li>
              <li><Badge variant="destructive" className="mr-2">Janeira deve</Badge> Saldo negativo: o Janeira tem valor a pagar ao parceiro</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RelatorioCustos;
